import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useLang, copy } from "@/i18n";
import { Reveal } from "@/components/Reveal";
import SignatureDraw from "@/components/SignatureDraw";
import bikeImg from "@/assets/intro-bike.jpg";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full bg-transparent border-b pb-3 font-mono-fmf text-base text-white placeholder:text-[var(--dim)]";
const labelCls =
  "block font-mono-fmf text-[13px] uppercase tracking-[0.3em] text-white mb-3";

export default function Footer() {
  const { lang } = useLang();
  const c = copy[lang].footer;
  const f = copy[lang].contact;
  const [form, setForm] = useState({
    nome: "",
    cognome: "",
    email: "",
    telefono: "",
    stato: "",
    citta: "",
    messaggio: "",
  });
  const [phase, setPhase] = useState("idle");

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (phase === "sending") return;
    setPhase("sending");
    try {
      await axios.post(`${API}/contact`, form);
      setPhase("done");
    } catch (_) {
      setPhase("idle");
    }
  };

  return (
    <footer
      data-testid="site-footer"
      className="border-t text-center"
      style={{ borderColor: "var(--line-soft)", background: "#050505" }}
    >
      <div className="px-6 md:px-14 pt-[8vh]">
        <img
          data-testid="footer-image"
          src={bikeImg}
          alt="TORQUE 1/1"
          className="w-full max-w-5xl mx-auto opacity-90"
        />
        <SignatureDraw className="h-24 md:h-28 w-auto mx-auto mt-12" />
      </div>

      <div id="dialogue" className="px-6 md:px-14 mt-[10vh]">
        <Reveal>
          <h2
            data-testid="contact-title"
            className="font-mono-fmf text-xl md:text-2xl uppercase tracking-[0.35em] text-[var(--bone)]"
          >
            {f.title}
          </h2>
        </Reveal>

        {phase === "done" ? (
          <p
            data-testid="contact-done"
            className="mt-14 font-mono-fmf text-base text-[var(--bone-dim)]"
          >
            {f.done}
          </p>
        ) : (
          <form
            data-testid="contact-form"
            onSubmit={submit}
            className="mt-14 max-w-3xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-10 text-left"
          >
            <div>
              <label htmlFor="contact-nome" className={labelCls}>{f.nome}</label>
              <input id="contact-nome" data-testid="contact-input-nome" required value={form.nome} onChange={set("nome")} className={inputCls} style={{ borderColor: "rgba(236,231,221,0.55)" }} />
            </div>
            <div>
              <label htmlFor="contact-cognome" className={labelCls}>{f.cognome}</label>
              <input id="contact-cognome" data-testid="contact-input-cognome" required value={form.cognome} onChange={set("cognome")} className={inputCls} style={{ borderColor: "rgba(236,231,221,0.55)" }} />
            </div>
            <div>
              <label htmlFor="contact-email" className={labelCls}>{f.email}</label>
              <input id="contact-email" data-testid="contact-input-email" type="email" required value={form.email} onChange={set("email")} className={inputCls} style={{ borderColor: "rgba(236,231,221,0.55)" }} />
            </div>
            <div>
              <label htmlFor="contact-telefono" className={labelCls}>{f.telefono}</label>
              <input id="contact-telefono" data-testid="contact-input-telefono" value={form.telefono} onChange={set("telefono")} className={inputCls} style={{ borderColor: "rgba(236,231,221,0.55)" }} />
            </div>
            <div>
              <label htmlFor="contact-stato" className={labelCls}>{f.stato}</label>
              <input id="contact-stato" data-testid="contact-input-stato" value={form.stato} onChange={set("stato")} className={inputCls} style={{ borderColor: "rgba(236,231,221,0.55)" }} />
            </div>
            <div>
              <label htmlFor="contact-citta" className={labelCls}>{f.citta}</label>
              <input id="contact-citta" data-testid="contact-input-citta" value={form.citta} onChange={set("citta")} className={inputCls} style={{ borderColor: "rgba(236,231,221,0.55)" }} />
            </div>
            <div className="md:col-span-2">
              <label htmlFor="contact-messaggio" className={labelCls}>{f.message}</label>
              <textarea id="contact-messaggio" data-testid="contact-input-messaggio" required rows={5} value={form.messaggio} onChange={set("messaggio")} className={`${inputCls} resize-none`} style={{ borderColor: "rgba(236,231,221,0.55)" }} />
            </div>
            <div className="md:col-span-2 text-center">
              <button
                data-testid="contact-submit-btn"
                type="submit"
                className="font-mono-fmf text-[15px] uppercase tracking-[0.4em] text-[var(--bone)] hover:text-[var(--bone-dim)] transition-colors duration-700 border-b border-transparent hover:border-[var(--bone-dim)] pb-2"
              >
                {phase === "sending" ? f.sending : f.submit}
              </button>
            </div>
          </form>
        )}
      </div>

      <div className="px-6 py-14 mt-[8vh] flex flex-col items-center gap-5">
        <p
          data-testid="footer-join"
          className="font-mono-fmf text-[15px] uppercase tracking-[0.4em] text-white"
        >
          {c.join}
        </p>
        <a
          data-testid="footer-email"
          href={`mailto:${c.email}`}
          className="font-mono-fmf text-base tracking-[0.15em] text-[var(--bone-dim)] hover:text-white transition-colors duration-700"
        >
          {c.email}
        </a>
        <img
          data-testid="footer-logo"
          src="/fmf-logo-white.png"
          alt="FMF"
          className="w-12 md:w-14 opacity-95 mt-12 mb-4"
        />
        <p
          data-testid="footer-line"
          className="font-mono-fmf text-[15px] tracking-[0.35em] uppercase text-[var(--bone-dim)]"
        >
          {c.line}
        </p>
        <p data-testid="footer-note" className="font-mono-fmf text-sm text-[var(--dim)]">
          {c.note}
        </p>
        <p className="font-mono-fmf text-[15px] tracking-[0.35em] text-[var(--dim)]">
          VAT 11919330966
        </p>
        <div className="mt-3 flex items-center gap-8">
          <Link
            data-testid="footer-link-privacy"
            to="/privacy"
            className="font-mono-fmf text-[13px] uppercase tracking-[0.3em] text-[var(--dim)] hover:text-white transition-colors duration-700"
          >
            Privacy
          </Link>
          <Link
            data-testid="footer-link-cookie"
            to="/cookie-policy"
            className="font-mono-fmf text-[13px] uppercase tracking-[0.3em] text-[var(--dim)] hover:text-white transition-colors duration-700"
          >
            Cookie Policy
          </Link>
        </div>
      </div>
    </footer>
  );
}
